import React from "react";

/**
 * Props (unchanged):
 *  - email, setEmail
 *  - password, setPassword
 *  - onSubmit()
 *  - switchToLogin()
 *  - error, message
 */
const SignupForm = ({
  email,
  setEmail,
  password,
  setPassword,
  onSubmit,
  switchToLogin,
  error,
  message,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") onSubmit();
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-slate-900">
      {/* Same background FX as SplashPage */}
      <BackgroundFX />

      {/* Centered glass card */}
      <main className="relative mx-auto max-w-7xl px-4 py-10 min-h-screen grid place-items-center">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-white/40 bg-white/70 backdrop-blur shadow-[0_10px_30px_-10px_rgba(2,6,23,0.25)]">
            <div className="p-6 sm:p-8">
              {/* BA badge + title */}
              <div className="flex items-center gap-3 mb-5">
                <div className="h-8 w-8 rounded-xl bg-slate-900 text-white grid place-items-center shadow-md">
                  <span className="text-xs font-bold tracking-wide">BA</span>
                </div>
                <div className="leading-tight">
                  <h1 className="text-lg font-semibold tracking-tight">Request Access</h1>
                  <p className="text-xs text-slate-600">We’ll notify you once approved</p>
                </div>
              </div>

              <label className="block text-xs mb-1 text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300 mb-3"
                placeholder="you@company.com"
                autoFocus
              />

              <label className="block text-xs mb-1 text-slate-600">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full rounded-xl border border-slate-300 bg_white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                style={{ background: "white" }}
                placeholder="Create a password"
              />

              {error && (
                <div className="mt-3 text-sm rounded-xl border border-red-200 bg-red-50 text-red-700 px-3 py-2">
                  {error}
                </div>
              )}
              {message && (
                <div className="mt-3 text-sm rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2">
                  {message}
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <button
                  onClick={onSubmit}
                  className="rounded-xl px-4 py-2 text-sm border border-slate-300 bg-white hover:bg-slate-50 shadow-sm"
                >
                  Request Signup
                </button>
                <button
                  onClick={switchToLogin}
                  className="text-sm text-slate-700 hover:underline underline-offset-2"
                  type="button"
                >
                  Back to login
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{cssFX}</style>
    </div>
  );
};

/* ---------- SplashPage background FX (copied) ---------- */
function BackgroundFX() {
  return (
    <>
      {/* soft grid sweep */}
      <div className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(60%_60%_at_50%_20%,black,transparent)]">
        <div className="absolute inset-0 opacity-[0.06] animate-grid-scan bg-[linear-gradient(to_right,rgba(2,6,23,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,0.1)_1px,transparent_1px)] bg-[length:24px_24px]" />
      </div>
      {/* aurora ribbons */}
      <div className="pointer-events-none absolute -top-1/3 left-1/2 -translate-x-1/2 h-[70vh] w-[120vw] blur-2xl opacity-60">
        <div className="aurora one" />
        <div className="aurora two" />
        <div className="aurora three" />
      </div>
      {/* bokeh dots */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white/30 blur-[2px] animate-float"
            style={{
              left: `${(i * 73) % 100}%`,
              top: `${(i * 41) % 100}%`,
              width: `${8 + ((i * 13) % 24)}px`,
              height: `${8 + ((i * 13) % 24)}px`,
              animationDelay: `${(i % 7) * 0.6}s`,
              opacity: 0.15 + ((i % 5) * 0.05),
            }}
          />
        ))}
      </div>
    </>
  );
}

/* ---------- Local CSS for FX (same as SplashPage) ---------- */
const cssFX = `
.aurora {
  position: absolute;
  inset: -20% -10% auto -10%;
  height: 120%;
  background: radial-gradient(60% 60% at 30% 40%, rgba(56,189,248,0.45), transparent 60%),
              radial-gradient(50% 50% at 70% 55%, rgba(168,85,247,0.35), transparent 60%),
              radial-gradient(40% 40% at 60% 20%, rgba(16,185,129,0.35), transparent 60%);
  filter: blur(40px) saturate(120%);
  transform: translateZ(0);
  mix-blend-mode: screen;
}
.aurora.two { animation: aurora-sway 11s ease-in-out infinite alternate; }
.aurora.three { animation: aurora-sway 14s ease-in-out infinite alternate-reverse; }
@keyframes aurora-sway { from { transform: translateY(-4%) rotate(-2deg); } to { transform: translateY(4%) rotate(2deg); } }
@keyframes float { 0% { transform: translateY(0) } 50% { transform: translateY(-8px) } 100% { transform: translateY(0) } }
.animate-float { animation: float 6s ease-in-out infinite; }
@keyframes grid-scan { 0% { background-position: 0px 0px, 0px 0px; } 100% { background-position: 48px 0px, 0px 48px; } }
.animate-grid-scan { animation: grid-scan 12s linear infinite; }
`;

export default SignupForm;
